import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeById, sumUniqueBy, isAged, ageSeverity, formatAge, compareAgeDesc } from '../../utils/deals';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { OfferingChart } from '../charts/OfferingChart';
import { useDeals } from '../../context/DealsContext';
import React, { Fragment } from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-295" data-mp="38adhf360i2y2">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('sd', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const aged = ip.filter(d => isAged(d));
  const scs = new Set(ip.map(d => d.sc)).size;
  const oppGnarr = sumUniqueBy(all, d => d.opp_gnarr);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeById(deals.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!d.close_q) return false;
      const [yr, qt] = d.close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedSorted = [...aged].sort(compareAgeDesc).slice(0, 8);
  const dealsSorted = [...ip].sort(compareAgeDesc);
  const allDealsSorted = [...all].sort(compareAgeDesc);
  return <div data-id="element-296" data-mp="2qf9blxmj7qd0">
      {/* Metrics */}
      <div className="mrow" data-id="element-297" data-mp="2qfit0wtigp73">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-298" data-mp="2uft60tfq0fvg" /> 
        <MetricCard label="Aged >90 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-299" data-mp="2zecup1lp85ri" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-300" data-mp="34dpippob3ivo" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-301" data-mp="39apm1stru7j3" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-302" data-mp="1eyizxyzca6c0">
        <div className="card" data-id="element-303" data-mp="1ezu625g9bq6a">
          <h3 data-id="element-304" data-mp="1f159u59pcxxe">Requests by solution</h3>
          <OfferingChart deals={all} data-id="element-305" data-mp="3s9u9wk47nkfe" />
        </div>
        <div className="card" data-id="element-306" data-mp="3sbf2w5avmvb5">
          <h3 data-id="element-307" data-mp="3niuc7cn9oybu">Aged deals (&gt;90 days) at risk</h3>
          {agedSorted.length > 0 ? <table className="tbl" data-id="element-308" data-mp="3jh0f1eca6cgf">
              <thead data-id="element-309" data-mp="3ji22kiahgrf1">
                <tr data-id="element-310" data-mp="3jhigcpdsvgv7">
                  <th style={{
                width: '30%'
              }} data-id="element-311" data-mp="3jew8eomzyzcg">Account</th>
                  <th style={{
                width: '20%'
              }} data-id="element-312" data-mp="3eiyyp168or8n">SC</th>
                  <th style={{
                width: '12%'
              }} data-id="element-313" data-mp="f2sfox1io1fd">Age</th>
                  <th style={{
                width: '18%'
              }} data-id="element-314" data-mp="f28y5hfs3f1v">GNARR</th>
                  <th style={{
                width: '12%'
              }} data-id="element-315" data-mp="b1gr8uudpm8r">Close Q</th>
                  <th style={{
                width: '8%'
              }} data-id="element-316" data-mp="b1ywggz0rast">Sub</th>
                </tr>
              </thead>
              <tbody data-id="element-317" data-mp="609hiq8oeczg">
                {agedSorted.map((d, rowPosition) => <Fragment key={`${p.n}-${rowPosition}`} data-id="element-318" data-mp="127z87j2qhp1" data-mpi={rowPosition}>
                  <tr key={`${d.id}`} data-id="element-319" data-mp="2qs1uyboll08h">
                    <td title={d.account} data-id="element-320" data-mp="2qt4hy8isuij4">{d.account.substring(0, 22)}</td>
                    <td title={d.sc} data-id="element-321" data-mp="2lsyzniyf68dp">{d.sc.split(' ')[0]}</td>
                    <td className="c-red" style={{
                  fontWeight: 600
                }} data-id="element-322" data-mp="2lvl6thheelve">{formatAge(d.age)}</td>
                    <td data-id="element-323" data-mp="difrif92xkqn">{fmtG(d.gnarr)}</td>
                    <td data-id="element-324" data-mp="ibiluploky7w">{d.close_q}</td>
                    <td data-id="element-325" data-mp="icjy2xefnqr7"><span className="badge b-gray" style={{
                    fontSize: 8
                  }} data-id="element-326" data-mp="icjzn1u2zytb">{d.sub_region}</span></td>
                  </tr>
                  </Fragment>)}
              </tbody>
            </table> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-327" data-mp="x8jf4xjit18h">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* In progress deals list */}
      <SectionHeader title="In-progress deals — full list" data-id="element-328" data-mp="nals2ddedahg" />
      <div className="tw" data-id="element-329" data-mp="na3p6xw8by0k">
        <table className="tbl" data-id="element-330" data-mp="s9g857jzckxv">
          <thead data-id="element-331" data-mp="s8n8eqjfuvp8">
            <tr data-id="element-332" data-mp="s859fm5e63dm">
              <th style={{
              width: '11%'
            }} data-id="element-333" data-mp="s7lpy103f6xh">DR ID</th>
              <th style={{
              width: '22%'
            }} data-id="element-334" data-mp="380b6ipa6uu9m">Account</th>
              <th style={{
              width: '15%'
            }} data-id="element-335" data-mp="382nnaz47myl3">SC Assigned</th>
              <th style={{
              width: '14%'
            }} data-id="element-336" data-mp="3d0p1p6qoy9q8">Offering</th>
              <th style={{
              width: '7%'
            }} data-id="element-337" data-mp="35g4pph0h79bn">Age</th>
              <th style={{
              width: '10%'
            }} data-id="element-338" data-mp="35etmpjeuu5ll">GNARR</th>
              <th style={{
              width: '9%'
            }} data-id="element-339" data-mp="30h1ueoaashgv">Close Q</th>
              <th style={{
              width: '7%'
            }} data-id="element-340" data-mp="30gjqd5he8yye">Region</th>
              <th style={{
              width: '5%'
            }} data-id="element-341" data-mp="3egkvix4oysjo">Sub</th>
            </tr>
          </thead>
          <tbody data-id="element-342" data-mp="39hqb5qr2svxg">
            {searchRows ?? dealsSorted.map((d, rowPosition) => <Fragment key={`${d.id}-${rowPosition}`} data-id="element-343" data-mp="39fnfaq38s8kt" data-mpi={rowPosition}>
              <tr key={d.id} data-id="element-344" data-mp="2lt71p7waid3s">
                <td data-id="element-345" data-mp="2lsf6hy6luhe1"><span style={{
                  fontSize: 9,
                  color: '#8892a0'
                }} data-id="element-346" data-mp="2lq2r9ss8el4o">{d.id}</span></td>
                <td title={d.account} data-id="element-347" data-mp="2hs53mue4073e">{d.account.substring(0, 24)}</td>
                <td title={d.sc} data-id="element-348" data-mp="2vi8fqsj9ykf4">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
                <td data-id="element-349" data-mp="2qribxjqhypmj">
                  <span className="badge b-blue" style={{
                  fontSize: 8
                }} data-id="element-350" data-mp="2qr05jua4ev0w">
                    {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
                  </span>
                </td>
                <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
                fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
              }} data-id="element-351" data-mp="1y1jm3m4haogt">{formatAge(d.age)}</td>
                <td data-id="element-352" data-mp="6x85fr59tl39">{fmtG(d.gnarr)}</td>
                <td data-id="element-353" data-mp="bp9g58msj3r6">{d.close_q}</td>
                <td data-id="element-354" data-mp="bozozhq0eqjw">{regionBadge(d.region)}</td>
                <td data-id="element-355" data-mp="brcdyeu6q16g"><span className="badge b-gray" style={{
                  fontSize: 8
                }} data-id="element-356" data-mp="brlvde15z00j">{d.sub_region}</span></td>
              </tr>
              </Fragment>)}
          </tbody>
        </table>
      </div>

      {/* Full deals list */}
      <SectionHeader title="Closed requests — full list" data-id="element-357" data-mp="3k4jdy4jndmt5" />
      <div className="tw" data-id="element-358" data-mp="3k75kq1ypryab">
        <table className="tbl" data-id="element-359" data-mp="3p45uonyopl6p">
          <thead data-id="element-360" data-mp="3p331289z94n3">
            <tr data-id="element-361" data-mp="3p2kxeqkzjo55">
              <th style={{
              width: '11%'
            }} data-id="element-362" data-mp="3p68qjomiq8jc">DR ID</th>
              <th style={{
              width: '22%'
            }} data-id="element-363" data-mp="3t5zn68yq7ivs">Account</th>
              <th style={{
              width: '15%'
            }} data-id="element-364" data-mp="3t3ekxiuin0xi">SC Assigned</th>
              <th style={{
              width: '14%'
            }} data-id="element-365" data-mp="14d59c3d5idj7">Offering</th>
              <th style={{
              width: '7%'
            }} data-id="element-366" data-mp="18fihszja8pjo">Age</th>
              <th style={{
              width: '10%'
            }} data-id="element-367" data-mp="18dxk511o8qhl">GNARR</th>
              <th style={{
              width: '9%'
            }} data-id="element-368" data-mp="2sxb35r2gvtbc">Close Q</th>
              <th style={{
              width: '7%'
            }} data-id="element-369" data-mp="2sx19nti7r40w">Region</th>
              <th style={{
              width: '5%'
            }} data-id="element-370" data-mp="2ovftnzadu9mh">Sub</th>
            </tr>
          </thead>
          <tbody data-id="element-371" data-mp="2k595u1np2t5a">
            {searchRows ?? allDealsSorted.map((d, rowPosition) => <Fragment key={`${d.id}-${rowPosition}`} data-id="element-372" data-mp="2k2wgh3flxgi7" data-mpi={rowPosition}>
              <tr key={d.id} data-id="element-373" data-mp="2f5d12076axoo">
                <td data-id="element-374" data-mp="2f41xo1hn3ry3"><span style={{
                  fontSize: 9,
                  color: '#8892a0'
                }} data-id="element-375" data-mp="2f41w3x1zrjvz">{d.id}</span></td>
                <td title={d.account} data-id="element-376" data-mp="2a8cuzpabpuxk">{d.account.substring(0, 24)}</td>
                <td title={d.sc} data-id="element-377" data-mp="256x8dsshzgd2">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
                <td data-id="element-378" data-mp="215v7l7ixmw8z">
                  <span className="badge b-blue" style={{
                  fontSize: 8
                }} data-id="element-379" data-mp="217engx0of5a8">
                    {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
                  </span>
                </td>
                <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
                fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
              }} data-id="element-380" data-mp="3vj39tk98ma4n">{formatAge(d.age)}</td>
                <td data-id="element-381" data-mp="2zgh7hgyahva9">{fmtG(d.gnarr)}</td>
                <td data-id="element-382" data-mp="34f265bxw86wl">{d.close_q}</td>
                <td data-id="element-383" data-mp="34e0m4hywga2q">{regionBadge(d.region)}</td>
                <td data-id="element-384" data-mp="34gv3u1qj3gsg"><span className="badge b-gray" style={{
                  fontSize: 8
                }} data-id="element-385" data-mp="34gv8if1h44ys">{d.sub_region}</span></td>
              </tr>
              </Fragment>)}
          </tbody>
        </table>
      </div>

    </div>;
}